import React from 'react';
import {Box, ContentTemplate, Progress, TextFieldForm} from 'web-library';
import {useTranslation} from 'react-i18next';


import {makeStyles} from 'tss-react/mui';
import {UseFormReturn} from 'react-hook-form';
import {FormControlLabel, Radio} from '@mui/material';
import {CandidateProfileResponseObject} from 'web-library/models/AccountManagementConfiguration';
import {ContactNumberFormItem} from '../../../components/form-items/ContactNumberFormItem';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import ResidentialAddress
  from '../../../modules/profile/components/CandidateProfileForm/HelperComponents/ResidentialAddress';
import ForeignAddress
  from '../../../modules/profile/components/CandidateProfileForm/HelperComponents/ForeignAddress';
import {getContactDisplayValue, getIsPreferredContactText} from '../../../utils/ComponentUtils';
import {useGetIExamMasterCode} from '../../../api-refactored/user/useGetIExamMasterCode';
import HideOrShow
  from '../../../modules/profile/components/CandidateProfileForm/HelperComponents/HideOrShow';
import {
  CandidateProfileResponseIsDisableInputs,
  CreateEditCandidateSchemaData,
} from './CreateEditCandidateSchema';
import {useCandidateContactInformation} from './useCreateEditViewCandidateAccountDetails';
import {
  ControllerRadioGroupWrapper
} from "../../../modules/profile/components/CandidateProfileForm/HelperComponents/ControllerRadioGroupWrapper";

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
    ({
      halfWidthLeftItem: {
        width: '50%',
        paddingRight: theme.spacing(2),
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          paddingRight: 0,
          marginBottom: theme.spacing(2),
        },
      },
      halfWidthRightItem: {
        width: '50%',
        paddingLeft: theme.spacing(2),
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          paddingLeft: 0,
          marginBottom: theme.spacing(2),
        },
      },
      responsiveFlexContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
      formElementContainer: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('md')]: {
          marginBottom: theme.spacing(3),
        },
        [theme.breakpoints.down('sm')]: {
          marginBottom: theme.spacing(2),
        },
        width: '100%',
      },
      keyValueContainer: {
        marginBottom: 0,
      },
    }));

export interface CandidateContactInformationProps {
  candidateProfileDetails?: CandidateProfileResponseObject;
  formHook?: UseFormReturn<CreateEditCandidateSchemaData>;
  isEditState: boolean;
  disabledFields?: CandidateProfileResponseIsDisableInputs;
}

const CandidateContactInformation = ({
                                       candidateProfileDetails,
                                       formHook,
                                       isEditState,
                                       disabledFields,
                                     }: CandidateContactInformationProps) => {
  const {classes} = useStyles();
  const {t} = useTranslation();

  const {data: dataIEXAM} = useGetIExamMasterCode();

  const {
    mobileNumberCountryCodeDefaultObject,
    telephoneNumberCountryCodeDefaultObject,
    isContactInformationValueUndefined,
  } = useCandidateContactInformation({
    iExamsData: dataIEXAM,
    candidateProfileDetails,
  });

  if (isContactInformationValueUndefined) {
    return (
        <ContentTemplate>
          <Progress color="secondary"/>
        </ContentTemplate>
    );
  }

  return (
      <Box className={classes.responsiveFlexContainer}>
        <ToggleDisplayEditFormItem
            isEditState={isEditState}
            containerClassName={classes.formElementContainer}
            labelText={t('admin->>UserAccountManagement.EmailAddress')}
            id="inputText-candidateProfile-email"
            valueText={candidateProfileDetails?.email}
            EditComponent={
              <TextFieldForm
                  form={formHook}
                  id="inputText-candidateProfile-email"
                  label={t('admin->>UserAccountManagement.EmailAddress')}
                  placeholder="Enter email address"
                  isSubLabelBelowLabel
                  defaultValue={candidateProfileDetails?.email}
                  name="email"
                  type="text"
                  error={!!formHook?.formState.errors.email}
                  helperText={formHook?.formState.errors?.email?.message?.toString()}
                  fullWidth
                  isRequired
              />
            }
        />

        <ControllerRadioGroupWrapper name="preferredTelephoneOrMobile"
                                     control={formHook?.control}
                                     defaultValue={
                                       candidateProfileDetails?.mobileNumberPreferred ||
                                       candidateProfileDetails?.mobileNumberPreferred === undefined
                                           ? 'M'
                                           : 'T'
                                     }
                                     className={classes.formElementContainer}>

          <ToggleDisplayEditFormItem
              id="mobileNo"
              isEditState={isEditState}
              labelText={t('admin->>UserAccountManagement.MobileNo')}
              valueText={getContactDisplayValue(
                  true,
                  candidateProfileDetails?.mobileNumberCountryCode,
                  candidateProfileDetails?.mobileNumber
              )}
              isPreferredLabelText={getIsPreferredContactText(
                  'M',
                  candidateProfileDetails?.mobileNumberPreferred ? 'M' : 'T'
              )}
              containerClassName={!isEditState ? classes.formElementContainer : ''}
              EditComponent={
                  formHook && (
                      <ContactNumberFormItem
                          id="candidateProfileMobile"
                          formHook={formHook}
                          label={t('admin->>UserAccountManagement.MobileNo')}
                          placeholder="Enter mobile no."
                          countryCode={{
                            name: 'mobileCountryCode',
                            error: !!formHook.formState.errors.mobileCountryCode,
                            helperText: formHook.formState.errors?.mobileCountryCode?.message?.toString(),
                            disabled: disabledFields?.mobileCountryCode,
                          }}
                          countryCodeisOptionEqualToValue={(option, value) => option.codeId === value.codeId}
                          number={{
                            name: 'mobileContactNumber',
                            error: !!formHook.formState.errors.mobileContactNumber,
                            helperText: formHook.formState.errors?.mobileContactNumber?.message?.toString(),
                            disabled: disabledFields?.mobileContactNumber,
                          }}
                          isSkipTransform
                          countryCodeDefaultValue={mobileNumberCountryCodeDefaultObject}
                          numberDefaultValue={candidateProfileDetails?.mobileNumber}
                          isRequired
                      />
                  )
              }
          />
          <HideOrShow
              isHide={!isEditState}
              Component={
                <FormControlLabel
                    value="M"
                    name="preferredTelephoneOrMobile"
                    control={<Radio/>}
                    label="Preferred contact"
                    className={classes.formElementContainer}
                />
              }
          />

          <ToggleDisplayEditFormItem
              id="telephoneNo"
              isEditState={isEditState}
              labelText={t('admin->>UserAccountManagement.TelephoneNo')}
              valueText={getContactDisplayValue(
                  true,
                  candidateProfileDetails?.telephoneCountryCode,
                  candidateProfileDetails?.telephoneNumber
              )}
              isPreferredLabelText={getIsPreferredContactText(
                  'T',
                  candidateProfileDetails?.mobileNumberPreferred ? 'M' : 'T'
              )}
              containerClassName={!isEditState ? classes.formElementContainer : ''}
              EditComponent={
                  formHook && (
                      <ContactNumberFormItem
                          id="candidateProfileMobile"
                          formHook={formHook}
                          label={t('admin->>UserAccountManagement.TelephoneNo')}
                          placeholder="Enter phone no."
                          countryCode={{
                            name: 'telephoneCountryCode',
                            error: !!formHook.formState.errors.telephoneCountryCode,
                            helperText: formHook.formState.errors?.telephoneCountryCode?.message?.toString(),
                            disabled: disabledFields?.telephoneCountryCode,
                          }}
                          countryCodeisOptionEqualToValue={(option, value) => option.codeId === value.codeId}
                          number={{
                            name: 'telephoneContactNumber',
                            error: !!formHook.formState.errors.telephoneContactNumber,
                            helperText: formHook.formState.errors?.telephoneContactNumber?.message?.toString(),
                            disabled: disabledFields?.telephoneContactNumber,
                          }}
                          isSkipTransform
                          countryCodeDefaultValue={telephoneNumberCountryCodeDefaultObject}
                          numberDefaultValue={candidateProfileDetails?.telephoneNumber}
                      />
                  )
              }
          />
          <HideOrShow
              isHide={!isEditState}
              Component={
                <FormControlLabel
                    value="T"
                    name="preferredTelephoneOrMobile"
                    control={<Radio/>}
                    label="Preferred contact"
                    className={classes.formElementContainer}
                />
              }
          />
        </ControllerRadioGroupWrapper>

        <ControllerRadioGroupWrapper name="preferredAddress"
                                     control={formHook?.control}
                                     defaultValue={
                                       candidateProfileDetails?.localAddressPreferred || candidateProfileDetails?.localAddressPreferred === undefined
                                           ? 'L'
                                           : 'F'
                                     }>
          <>
            <ResidentialAddress
                isEditState={isEditState}
                formHook={formHook}
                isDisableInputs={disabledFields}
                isHideExtraFormInfo
                isNonNational
                formDefaultValues={{
                  preferredAddress: candidateProfileDetails?.localAddressPreferred ? 'L' : 'F',
                  houseOrBlkNo: candidateProfileDetails?.localAddressBlockNumber,
                  streetName: candidateProfileDetails?.localAddressStreetName,
                  building: candidateProfileDetails?.localAddressBuildingName,
                  floor: candidateProfileDetails?.localAddressFloorNumber,
                  unit: candidateProfileDetails?.localAddressUnitNumber,
                  postalCode: candidateProfileDetails?.localAddressPostalCode,
                }}
                formDefaultValuesViewDetails={{
                  preferredAddress: candidateProfileDetails?.localAddressPreferred ? 'L' : 'F',
                  houseOrBlkNo: candidateProfileDetails?.localAddressBlockNumber,
                  streetName: candidateProfileDetails?.localAddressStreetName,
                  building: candidateProfileDetails?.localAddressBuildingName,
                  floor: candidateProfileDetails?.localAddressFloorNumber,
                  unit: candidateProfileDetails?.localAddressUnitNumber,
                  postalCode: candidateProfileDetails?.localAddressPostalCode,
                }}
                isRequired
            />
            <Box className={classes.formElementContainer}>
              <ForeignAddress
                  isEditState={isEditState}
                  formHook={formHook}
                  formDefaultValues={{
                    preferredAddress: candidateProfileDetails?.localAddressPreferred ? 'L' : 'F',
                    foreignAddress1: candidateProfileDetails?.foreignAddress1,
                    foreignAddress2: candidateProfileDetails?.foreignAddress2,
                    foreignAddress3: candidateProfileDetails?.foreignAddress3,
                  }}
              />
            </Box>
          </>
        </ControllerRadioGroupWrapper>
      </Box>
  );
};

export default CandidateContactInformation;
